import React, { ReactNode, useCallback, useState } from "react";
import { Place } from "react-tooltip";
import styled from "styled-components";

import { TooltipWrapper } from "src/shared/components";
import { getPerkStatusTooltip } from "src/views/sponsor/perks/constants/perkStatusTooltips";
import { PerkStatus } from "src/views/sponsor/perks/types";

const TOOLTIP_WIDTH_ESTIMATE = 240;
const VIEWPORT_MARGIN = 8;

interface PerkStatusTooltipProps {
  status: PerkStatus;
  id: string;
  gap?: number;
  preferredDirection?: Place;
  children: ReactNode;
}

const PerkStatusTooltip: React.FC<PerkStatusTooltipProps> = ({
  status,
  id,
  gap = 6,
  preferredDirection = "left",
  children,
}) => {
  const { title, description } = getPerkStatusTooltip(status);
  const [direction, setDirection] = useState<Place>(preferredDirection);

  const updateDirection = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const { left, right } = event.currentTarget.getBoundingClientRect();

      if (preferredDirection === "right") {
        const wouldOverflowRight =
          right + TOOLTIP_WIDTH_ESTIMATE > window.innerWidth - VIEWPORT_MARGIN;
        setDirection(wouldOverflowRight ? "left" : "right");
        return;
      }

      const wouldOverflowLeft = left - TOOLTIP_WIDTH_ESTIMATE < VIEWPORT_MARGIN;
      setDirection(wouldOverflowLeft ? "right" : "left");
    },
    [preferredDirection]
  );

  return (
    <TooltipHoverTarget onMouseEnter={updateDirection}>
      <TooltipWrapper
        id={id}
        icon={<TooltipTrigger $gap={gap}>{children}</TooltipTrigger>}
        direction={direction}
        color="textDark"
        padding={16}
        content={
          <TooltipContent>
            <TooltipTitle>{title}</TooltipTitle>
            <TooltipDescription>{description}</TooltipDescription>
          </TooltipContent>
        }
      />
    </TooltipHoverTarget>
  );
};

const TooltipHoverTarget = styled.span`
  display: inline-flex;
  min-width: 0;

  & > span {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const TooltipTrigger = styled.span<{ $gap: number }>`
  display: inline-flex;
  align-items: center;
  column-gap: ${({ $gap }) => $gap}px;
  min-width: 0;
  cursor: default;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 12px;
  line-height: normal;
  color: ${({ theme }) => theme.globalConstants.color.white};
  word-break: break-word;
`;

const TooltipTitle = styled.p`
  margin: 0;
  font-weight: 700;
`;

const TooltipDescription = styled.p`
  margin: 0;
  font-weight: 500;
`;

export default PerkStatusTooltip;
